/* Shared conversation primitives for the workspace and text consultations. */
import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { Ic } from './Icons.jsx';
import { renderMarkdown } from '../lib/markdown.js';
import { useConversationScroll } from '../lib/useConversationScroll.js';
import { shouldSendOnEnter } from '../lib/composerKeyboard.js';
import { splitThinking } from '../lib/thinking.js';

function Composer({ value, onChange, onSend, onVoice, onVideo, canVideo = true,
  dictating, onDictate, locale = 'en', disabled = false, placeholder, generating = false,
  onStop, stopping = false, canStop = true, sendDisabled = false, onFocus, maxLength, ariaLabel }) {
  const taRef = useRef(null);
  const tr = (zh, en) => locale.startsWith('zh') ? zh : en;
  useLayoutEffect(() => {
    const ta = taRef.current;
    const resize = () => {
      if (!ta) return;
      const mobile = window.matchMedia('(max-width: 767px)').matches;
      const max = mobile ? Math.min(160, (window.visualViewport?.height || window.innerHeight) * .3) : 200;
      ta.style.height = 'auto';
      ta.style.height = Math.max(28, Math.min(ta.scrollHeight, max)) + 'px';
    };
    resize();
    window.addEventListener('resize', resize);
    window.visualViewport?.addEventListener('resize', resize);
    return () => { window.removeEventListener('resize', resize); window.visualViewport?.removeEventListener('resize', resize); };
  }, [value]);
  const canSend = !disabled && !sendDisabled && !generating && !dictating && value.trim().length > 0;
  const submit = () => { if (canSend) onSend(); };
  const micLabel = dictating ? tr('结束语音输入并转写', 'Stop voice input and transcribe') : tr('开启语音输入', 'Start voice input');
  const sendLabel = tr('发送', 'Send');
  return <div className="composer">
    <textarea ref={taRef} rows={1} value={value} disabled={disabled} onFocus={onFocus} maxLength={maxLength}
      aria-label={ariaLabel || tr('消息', 'Message')} placeholder={placeholder || tr('从此刻的想法开始…', 'Start with what’s on your mind…')}
      onChange={event => onChange(event.target.value)} onKeyDown={event => {
        if (shouldSendOnEnter(event, window.matchMedia('(pointer: coarse)').matches)) { event.preventDefault(); submit(); }
      }} />
    <div className="composer-row">
      <div className="composer-tools">
        {onDictate && <button type="button" className={'round-btn ghost' + (dictating ? ' recording' : '')}
          title={micLabel} aria-label={micLabel} aria-pressed={!!dictating} disabled={disabled || generating}
          onClick={onDictate}><Ic.Mic size={20} /></button>}
        {canVideo && onVideo && <button type="button" className="round-btn ghost" title={tr('视频通话', 'Video call')}
          aria-label={tr('视频通话', 'Video call')} disabled={disabled || generating || dictating} onClick={onVideo}><Ic.Video size={20} /></button>}
        {onVoice && <button type="button" className="composer-voice" title={tr('语音通话', 'Voice call')}
          aria-label={tr('语音通话', 'Voice call')} disabled={disabled || generating || dictating} onClick={onVoice}>
          <Ic.Waveform size={20} /><span>{tr('语音', 'Voice')}</span>
        </button>}
      </div>
      {generating && onStop ? <button type="button" className="round-btn send" disabled={stopping || !canStop}
        title={stopping ? tr('正在停止…', 'Stopping…') : tr('停止回复', 'Stop response')}
        aria-label={stopping ? tr('正在停止…', 'Stopping…') : tr('停止回复', 'Stop response')} onClick={onStop}><Ic.Stop size={20} /></button>
        : <button type="button" className="round-btn send" title={sendLabel} aria-label={sendLabel} disabled={!canSend} onClick={submit}><Ic.ChevUp size={22} /></button>}
    </div>
    {dictating && <div className="composer-recording" role="status"><span className="recording-dot" aria-hidden="true" />
      {tr('正在录音，再次点击麦克风即可转为文字。', 'Recording. Tap the microphone again to transcribe.')}</div>}
  </div>;
}

function MsgActions({ text, locale }) {
  const [copyState, setCopyState] = useState('idle');
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const tr = (zh, en) => locale.startsWith('zh') ? zh : en;
  async function copy() {
    try {
      if (!navigator.clipboard) throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(text); setCopyState('copied');
    } catch { setCopyState('error'); }
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopyState('idle'), 2000);
  }
  return <div className="msg-actions">
    <button type="button" className="act-btn" title={tr('复制回复', 'Copy response')} aria-label={tr('复制回复', 'Copy response')} onClick={copy}>
      {copyState === 'copied' ? <Ic.Check size={18} /> : <Ic.Copy size={18} />}
    </button>
    <span role="status">{copyState === 'copied' ? tr('已复制', 'Copied') : copyState === 'error' ? tr('复制失败，请选择文字复制', 'Could not copy. Select the text to copy.') : ''}</span>
  </div>;
}

function Message({ m, locale = 'en' }) {
  const tr = (zh, en) => locale.startsWith('zh') ? zh : en;
  if (m.role === 'user') return <article className="msg user" aria-label={tr('你', 'You')}><div className="bubble-user">{m.text}</div></article>;
  const view = splitThinking(m.text, { expected: !!m.thinkingExpected, streaming: !!m.streaming });
  const reasoning = m.reasoning || view.reasoning;
  const interrupted = !m.streaming && (m.stopped || m.error) && !view.answer;
  return <article className="msg assistant" aria-label={tr('助手', 'Assistant')}>
    <div className="a-body">
      {(reasoning || view.pending) && <details className="thinking-disclosure"><summary>{interrupted ? tr('思考未完成', 'Thinking interrupted') : view.pending ? tr('正在思考', 'Thinking…') : tr('思考过程', 'Thinking')}</summary><div className="thinking-content">{reasoning}</div></details>}
      {!view.answer && m.streaming && !view.pending ? <div className="reply-status" role="status"><span className="typing-dots" aria-hidden="true"><i /><i /><i /></span>{tr('正在回复…', 'Responding…')}</div>
        : <div className="prose" dangerouslySetInnerHTML={{ __html: renderMarkdown(view.answer) }} />}
      {m.stopped && <p className="message-state">{tr('本轮回复已停止', 'Response stopped')}</p>}
      {m.error && <div className="ap-error" role="alert">{m.error.message || tr('本轮回复未完成。', 'This response did not complete.')}</div>}
      {!m.streaming && view.answer && <MsgActions text={view.answer} locale={locale} />}
    </div>
  </article>;
}

function ConversationThread({ messages, locale = 'en', resetKey, emptyText }) {
  const { scrollRef, contentRef, onScroll, away, jump } = useConversationScroll(messages, resetKey);
  return <div className="conversation-scroll">
    <div className="thread" ref={scrollRef} onScroll={onScroll} tabIndex={0} role="region" aria-label={locale.startsWith('zh') ? '对话内容' : 'Conversation'}>
      <div className="thread-inner" ref={contentRef}>
        {!messages.length && emptyText && <p className="reply-status" role="status">{emptyText}</p>}
        {messages.map(message => <Message key={message.id} m={message} locale={locale} />)}
      </div>
    </div>
    {away && <button type="button" className="jump-latest" onClick={jump}><Ic.Chev size={16} />{locale.startsWith('zh') ? '回到最新' : 'Jump to latest'}</button>}
  </div>;
}

function ChatView({ messages, draft, setDraft, onSend, onVoice, onVideo, canVideo = true,
  dictating, onDictate, locale = 'en', conversationKey, onStop, stopping, canStop, disabled = false }) {
  const tr = (zh, en) => locale.startsWith('zh') ? zh : en;
  const empty = messages.length === 0;
  const generating = messages.some(message => message.streaming);
  const composer = <Composer value={draft} onChange={setDraft} onSend={onSend} onVoice={onVoice} onVideo={onVideo}
    disabled={disabled} canVideo={canVideo} dictating={dictating} onDictate={onDictate} locale={locale} generating={generating}
    onStop={onStop} stopping={stopping} canStop={canStop} />;
  return <div className={'chat-wrap' + (empty ? ' chat-empty' : '')}>
    {empty ? <div className="hero">
      <div className="welcome-copy"><h1>{tr('此刻，你想聊些什么？', 'What’s on your mind?')}</h1>
        <p>{tr('不必组织好语言，从一个想法、一种感受开始。', 'Start with a thought or a feeling. Take your time.')}</p></div>
      <div className="hero-composer">{composer}</div>
      <p className="welcome-note">{tr('想延续一个话题？在侧栏中开始或继续咨询。', 'For an ongoing conversation, start or continue therapy in the sidebar.')}</p>
    </div> : <>
      <ConversationThread messages={messages} locale={locale} resetKey={conversationKey} />
      <div className="thread-composer">{composer}</div>
      <p className="disclaimer">{tr('AI 的回复可能有误，请结合自己的情况判断。', 'AI responses may be inaccurate. Use your own judgment.')}</p>
    </>}
  </div>;
}

export { Composer, ChatView, Message, ConversationThread };
